import { z } from "zod";
import { cpfSchema, isoDateSchema, moneySchema, trimmed } from "./common";

export const invoiceStatuses = ["pending", "issued", "cancelled"] as const;
export const invoiceStatusSchema = z.enum(invoiceStatuses);
export type InvoiceStatus = z.infer<typeof invoiceStatusSchema>;

const positiveMoney = moneySchema.refine((value) => value > 0, "Informe um valor maior que zero");

export const issueInvoiceInputSchema = z.object({ cpf: cpfSchema, value: positiveMoney, description: trimmed(300) });
export type IssueInvoiceInput = z.infer<typeof issueInvoiceInputSchema>;

export const manualInvoiceInputSchema = z.object({ number: trimmed(60), issuedAt: isoDateSchema, value: positiveMoney, cpf: z.union([z.literal(""), cpfSchema]).default("") });
export type ManualInvoiceInput = z.infer<typeof manualInvoiceInputSchema>;

export const cancelInvoiceInputSchema = z.object({ reason: trimmed(200) });
export type CancelInvoiceInput = z.infer<typeof cancelInvoiceInputSchema>;

export interface Invoice {
  id: string;
  appointmentId: string;
  appointmentDate: string;
  appointmentType: string;
  clientId: string;
  clientName: string;
  clientCpf: string;
  clientPhone: string;
  status: InvoiceStatus;
  number: string | null;
  accessKey: string | null;
  /** Para pendentes, o valor combinado da consulta; para as demais, o valor da nota. */
  value: number;
  description: string;
  issuedAt: string | null;
  issuedByName: string | null;
  manual: boolean;
  cancelledAt: string | null;
  cancelReason: string | null;
  lastError: string | null;
  /** A consulta ainda aponta para esta nota (pode receber uma substituta). */
  isCurrent: boolean;
}

export const fiscalEnvironments = ["restricted", "production"] as const;
export const fiscalEnvironmentSchema = z.enum(fiscalEnvironments);
export type FiscalEnvironment = z.infer<typeof fiscalEnvironmentSchema>;

/** Endereços da API do Sistema Nacional NFS-e por ambiente; confirmar no manual oficial antes da integração real. */
export const fiscalDefaultEndpoints: Record<FiscalEnvironment, string> = {
  restricted: "https://sefin.producaorestrita.nfse.gov.br/SefinNacional",
  production: "https://sefin.nfse.gov.br/SefinNacional",
};

export const fiscalSettingsInputSchema = z.object({
  providerName: z.string().trim().max(150),
  cnpj: z.string().trim().max(20),
  city: z.string().trim().max(80),
  serviceCode: z.string().trim().max(40),
  descriptionTemplate: trimmed(200),
  environment: fiscalEnvironmentSchema,
  /** Só tem efeito com o emissor simulado: força a próxima emissão a falhar, para validar o caminho de erro. */
  simulateFailure: z.boolean().default(false),
  endpoint: z.string().trim().max(200).regex(/^https:\/\/[^\s/]+\.[^\s]+$/, "Informe um endereço completo começando com https://").transform((value) => value.replace(/\/+$/, "")),
});
export type FiscalSettingsInput = z.infer<typeof fiscalSettingsInputSchema>;

export type CertificateStatus = "missing" | "valid" | "expiring" | "expired";

export interface FiscalSettings extends FiscalSettingsInput {
  issuer: string;
  certificate: { fileName: string; subject: string; validUntil: string; installedByName: string; installedAt: string } | null;
  certificateStatus: CertificateStatus;
  certificateDaysLeft: number | null;
}

export const certificateInputSchema = z.object({
  fileName: z.string().trim().max(120).regex(/\.(pfx|p12)$/i, "O arquivo precisa terminar em .pfx ou .p12"),
  /** Conteúdo do arquivo em base64 (certificados A1 têm poucos KB). */
  contentBase64: z.string().min(1).max(200_000),
  password: z.string().min(1).max(200),
});
export type CertificateInput = z.infer<typeof certificateInputSchema>;

export function describeService(template: string, appointment: { type: string; date: string }): string {
  const [year, month, day] = appointment.date.split("-");
  return template.replaceAll("{tipo}", appointment.type).replaceAll("{data}", `${day}/${month}/${year}`);
}
